import * as Slider from "@radix-ui/react-slider";
import Tooltip from "@material-ui/core/Tooltip";
import { useRef } from "react";
import cn from "classnames";
import useTooltipStyles from "./style";
import { hideSelectionTemporarily, updateShapeMouseDown, updateShapeMouseUp } from "../../utils";

interface SidebarSliderProps {
  max: number;
  min: number;
  step: number;
  title: string;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  editor: any;
  isExistColorMode?: any;
  selectedShape: any;
  addPx?: boolean;
  twoRangeColor?: boolean;
  prop: string;
  setSelectedShape: any;
  zeroToOne?: boolean;
  tooltip?: string;
  placement?: any;
  selectedShapes?: any[];
  popoverStyle?: boolean;
}

const SideBarSlider = ({
  max,
  min,
  step,
  title,
  sliderValue: propSliderValue,
  setSliderValue,
  editor,
  selectedShape,
  isExistColorMode,
  addPx,
  twoRangeColor,
  prop,
  setSelectedShape,
  zeroToOne,
  tooltip = "",
  placement = "top",
  selectedShapes = [],
  popoverStyle,
}: SidebarSliderProps) => {
  const isOpacity = title === "Opacity" || title === "Shadow Opacity" || title === "Inner Shadow Opacity";
  const isBlur = title === "Layer blur";
  const isLetterSpacing = title === "Letter spacing";
  const isLineHeight = title === "Line height";
  const isRounded = title === "Rounded";

  let normalizedValue = isOpacity ? propSliderValue * 100 : propSliderValue;

  const classesTooltip = useTooltipStyles();
  const divRef = useRef<HTMLDivElement>(null);

  // Adjust normalizedValue for two range color mode when zeroToOne is true
  if (zeroToOne && twoRangeColor) {
    if (normalizedValue < 1) {
      // Scale normalizedValue to a range of 0 to 50 if it's less than 1
      normalizedValue = ((normalizedValue - min) / (1 - min)) * 50;
    } else {
      // Scale normalizedValue to a range of 50 to 100 if it's 1 or more
      normalizedValue = 50 + ((normalizedValue - 1) / (max - 1)) * 50;
    }
  }

  if (isLetterSpacing) {
    if (propSliderValue < 0) {
      normalizedValue = ((propSliderValue - min) / (0 - min)) * 50;
    } else {
      normalizedValue = 50 + ((propSliderValue - 0) / (max - 0)) * 50;
    }
  }

  // Adjust normalizedValue for isBlur
  if (isBlur) {
    normalizedValue = (propSliderValue / 50) * 100;
  }

  /**
   * Handles changes to the slider value, normalizing the value based on various conditions
   * and updating the shape properties accordingly.
   *
   * @param value - The new slider value as an array of numbers.
   */
  const handleSliderChange = (value: number[]) => {
    let newValue = value[0];

    // Adjust newValue based on zeroToOne and twoRangeColor conditions
    if (zeroToOne && twoRangeColor) {
      newValue = newValue < 50 ? min + (newValue / 50) * (1 - min) : 1 + ((newValue - 50) / 50) * (max - 1);
    }

    // Adjust newValue based on isLetterSpacing condition
    if (isLetterSpacing) {
      if (newValue < 50) {
        newValue = min + (newValue / 50) * (0 - min);
      } else {
        newValue = 0 + ((newValue - 50) / 50) * (max - 0);
      }
    }

    // Adjust newValue for isBlur
    if (isBlur) {
      newValue = (newValue / 100) * 50;
    }

    // Normalize for opacity if applicable
    if (isOpacity) {
      newValue /= 100;
    }

    newValue = parseFloat(newValue.toFixed(1));
    setSliderValue(newValue);

    // Skip shape updates if this is just a drawing weight setting
    if (prop === "drawingWeight") {
      return;
    }

    selectedShapes.forEach((shape) => {
      if (!shape || !shape.props.hasOwnProperty(prop)) return;

      if (prop.startsWith("outShadowArray") || prop.startsWith("inShadowArray")) {
        const updatedProps = { ...shape.props };
        const [_, shadowId, propKey] = prop.split(".");
        const shadowType = prop.startsWith("outShadowArray") ? "outShadowArray" : "inShadowArray";
        if (shadowId && propKey) {
          updatedProps[shadowType] = {
            ...updatedProps[shadowType],
            [shadowId]: {
              ...updatedProps[shadowType][shadowId],
              [propKey]: newValue,
              dx: updatedProps[shadowType][shadowId].dx,
              dy: updatedProps[shadowType][shadowId].dy,
            },
          };
        }

        editor.updateShape({ ...shape, props: updatedProps });
        setSelectedShape({ ...shape, props: updatedProps });
      } else {
        const updatedProps = { ...shape.props, [prop]: newValue };
        editor.updateShape({ ...shape, props: updatedProps });
        setSelectedShape({ ...shape, props: updatedProps });
      }

      // hideSelectionTemporarily(shape, editor);
    });
  };

  /**
   * Handles changes to the text input value, normalizing the value based on whether it's an opacity value
   * and updating the shape properties accordingly.
   *
   * @param e - The change event.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value === "" ? 0 : parseFloat(e.target.value);
    if (isNaN(inputValue)) return;

    if (isOpacity) {
      inputValue = inputValue / 100; // Convert back to 0-1 range
    }

    setSliderValue(inputValue);

    // Skip shape updates if this is just a drawing weight setting
    if (prop === "drawingWeight") {
      return;
    }

    selectedShapes.forEach((shape) => {
      if (!shape || !shape.props.hasOwnProperty(prop)) return;

      if (prop.startsWith("outShadowArray") || prop.startsWith("inShadowArray")) {
        const updatedProps = { ...shape.props };
        const [_, shadowId, propKey] = prop.split(".");
        const shadowType = prop.startsWith("outShadowArray") ? "outShadowArray" : "inShadowArray";
        if (shadowId && propKey) {
          updatedProps[shadowType] = {
            ...updatedProps[shadowType],
            [shadowId]: {
              ...updatedProps[shadowType][shadowId],
              [propKey]: inputValue,
              dx: updatedProps[shadowType][shadowId].dx,
              dy: updatedProps[shadowType][shadowId].dy,
            },
          };
        }

        editor.updateShape({ ...shape, props: updatedProps });
        setSelectedShape({ ...shape, props: updatedProps });
      } else {
        const updatedProps = { ...shape.props, [prop]: inputValue };
        editor.updateShape({ ...shape, props: updatedProps });
        setSelectedShape({ ...shape, props: updatedProps });
      }

      hideSelectionTemporarily(shape, editor);
    });
  };

  // Function to get the clamped slider value
  const getClampedSliderValue = () => {
    if (zeroToOne && twoRangeColor) {
      if (propSliderValue < 1) {
        return Math.min(50, ((propSliderValue - min) / (1 - min)) * 50);
      }
      return Math.min(100, 50 + ((propSliderValue - 1) / (max - 1)) * 50);
    }

    if (isBlur) {
      return Math.min(100, (propSliderValue / 50) * 100);
    }

    if (isLetterSpacing) {
      if (propSliderValue < 0) {
        return Math.min(50, ((propSliderValue - min) / (0 - min)) * 50);
      } else if (propSliderValue === 0) {
        return 50; // Center the slider thumb when the value is zero
      } else {
        return Math.min(100, 50 + ((propSliderValue - 0) / (max - 0)) * 50);
      }
    }

    return isOpacity
      ? Math.min(max * 100, Math.max(min * 100, normalizedValue))
      : Math.min(max, Math.max(min, normalizedValue));
  };

  const getRangeStyle = () => {
    let percentage;

    if (zeroToOne && twoRangeColor) {
      percentage = normalizedValue;
    } else {
      percentage =
        ((normalizedValue - (isOpacity ? min * 100 : min)) / (isOpacity ? (max - min) * 100 : max - min)) * 100;
    }

    if (isBlur) {
      percentage = (normalizedValue / 100) * 100;
    }

    if (isLetterSpacing) {
      if (propSliderValue < 0) {
        percentage = ((propSliderValue - min) / (0 - min)) * 50;
      } else {
        percentage = 50 + ((propSliderValue - 0) / (max - 0)) * 50;
      }
    }

    if (twoRangeColor) {
      return {
        left: percentage < 50 ? `${percentage}%` : "50%",
        width: `${Math.abs(percentage - 50)}%`,
        backgroundColor: percentage < 50 ? "#FF798D80" : "#4F5B94",
      };
    }

    return {
      width: `${percentage}%`,
      backgroundColor: "#4F5B94",
    };
  };

  // Format slider value based on whether it's an opacity value
  const formatSliderValue = (value: number, isOpacity: boolean): string => {
    if (value === undefined || value === null) {
      return "";
    }

    if (isOpacity) {
      const opacityValue = value * 100;
      return opacityValue.toFixed(Number.isInteger(opacityValue) ? 0 : 1);
    }

    if (isBlur) {
      const blurValue = (value / 50) * 100;
      return blurValue.toFixed(Number.isInteger(blurValue) ? 0 : 1);
    }

    return Number.isInteger(value) ? value.toString() : value.toFixed(1);
  };

  const getMaxValue = () => {
    if (isBlur) {
      return 100;
    } else if (zeroToOne && twoRangeColor) {
      return 100;
    } else if (isOpacity) {
      return max * 100;
    } else if (isLineHeight) {
      return max;
    } else if (isLetterSpacing) {
      return 100;
    } else {
      return max;
    }
  };

  const getMinValue = () => {
    switch (true) {
      case isBlur:
        return 0;
      case zeroToOne && twoRangeColor:
        return 0;
      case isOpacity:
        return min * 100;
      case isLineHeight:
        return min;
      case isLetterSpacing:
        return 0;
      default:
        return min;
    }
  };

  const handleMouseDown = () => {
    if (selectedShape) {
      updateShapeMouseDown(selectedShape, editor);
    }
  };

  const handleMouseUp = () => {
    if (selectedShape) {
      updateShapeMouseUp(selectedShape, editor);
    }
  };

  return (
    <Tooltip
      title={tooltip}
      arrow
      placement={placement}
      classes={{ tooltip: classesTooltip.tooltip, arrow: classesTooltip.arrow }}
    >
      <div key={title} className="relative">
        <div ref={divRef} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} style={{ display: "none" }}></div>
        <div className="relative w-full flex items-center justify-between">
          <p className={cn(popoverStyle && "text-[9px]", "text-[13px] text-[#707087]")}>{title}</p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={propSliderValue !== undefined ? formatSliderValue(propSliderValue, isOpacity) : ""}
              onChange={handleInputChange}
              step={step}
              inputMode="decimal"
              style={{
                border: "1px solid #E4E4EA",
              }}
              className={cn(
                popoverStyle ? "text-[#0C172C] h-[10px]" : "text-[#707087] h-[10px] py-[6px]",
                "w-[18px] text-[7px] text-center rounded-[2px] no-arrows"
              )}
              pattern="-?[0-9]*[.,]?[0-9]*"
              onInput={(e) => {
                const target = e.target as HTMLInputElement;
                if (isOpacity || isRounded) {
                  if (parseFloat(target.value) > 100) {
                    target.value = "100";
                  }
                  if (target.value === "0" && (target.selectionStart ?? 0) > 1) {
                    target.value = ""; // Clear the value
                  }
                }
              }}
              onBlur={(e) => {
                if (isOpacity && e.target.value === "") {
                  e.target.value = "0"; // Set the value back to 0 if the input is empty
                }
              }}
            />
          </div>
        </div>
        <div className="absolute -top-1 left-1/2 -translate-x-1/2">
          {isLineHeight && (
            <svg width="23" height="20" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="0.219818" y1="18.9219" x2="22.8745" y2="18.9219" stroke="#707087" stroke-width="0.331975" />
              <line x1="0.219818" y1="0.398465" x2="22.8745" y2="0.398465" stroke="#707087" stroke-width="0.331975" />
              <path
                d="M15.3422 14.8145L14.3597 12.3499H8.73127L7.74879 14.8145H6.21679L10.6796 3.70746H12.3947L16.8742 14.8145H15.3422ZM11.5455 4.93972L9.13092 11.1177H13.9434L11.5455 4.93972Z"
                fill="#707087"
              />
            </svg>
          )}
        </div>
        <div className="absolute -top-1 left-1/2 -translate-x-1/2">
          {isLetterSpacing && (
            <svg width="20" height="23" viewBox="0 0 20 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <line x1="0.452243" y1="0.03125" x2="0.452243" y2="22.686" stroke="#707087" stroke-width="0.331975" />
              <line x1="18.9738" y1="0.111328" x2="18.9738" y2="22.7661" stroke="#707087" stroke-width="0.331975" />
              <path
                d="M13.3421 16.4668L12.3596 14.0023H6.73115L5.74867 16.4668H4.21667L8.67945 5.3598H10.3946L14.8741 16.4668H13.3421ZM9.54536 6.59206L7.1308 12.77H11.9433L9.54536 6.59206Z"
                fill="#707087"
              />
            </svg>
          )}
        </div>
        <Slider.Root
          className={cn(popoverStyle && "h-[6px] -mb-2", "SliderRoot")}
          value={[getClampedSliderValue()]}
          max={getMaxValue()}
          min={getMinValue()}
          step={isBlur ? step * 1 : isOpacity ? step * 100 : step}
          onValueChange={handleSliderChange}
          onPointerDown={handleMouseDown}
          onPointerUp={handleMouseUp}
        >
          <Slider.Track className={cn(popoverStyle && "h-[2px]", "SliderTrack overflow-hidden")}>
            {twoRangeColor ? (
              <div className="SliderRange" style={getRangeStyle()} />
            ) : (
              <Slider.Range className="SliderRange" style={getRangeStyle()} />
            )}
          </Slider.Track>
          <Slider.Thumb
            className={cn(popoverStyle && "w-[6px] h-[6px]", "SliderThumb !overflow-visible")}
            aria-label={title}
          />
        </Slider.Root>
      </div>
    </Tooltip>
  );
};

export default SideBarSlider;
